import has from 'lodash/has';

import MarkerUtils from '@shared/publication/dom-utils/marker-utils.mjs';
import LayoutUtils from '@shared/publication/dom-utils/layout-utils.mjs';
import Highlighter from '@shared/publication/highlighter.mjs';
import Locator from '@shared/publication/locator.mjs';
import CustomErrorEnum from '@shared/enums/CustomErrorEnum.mjs';
import SelectionDirectionEnum from '@shared/enums/SelectionDirectionEnum.mjs';
import SelectionConstantUtils from './SelectionConstantUtils.mjs';
import { SelectionModeEnum } from './SelectionModeEnum';

const START_BORDER_CLASSNAME = 'start-border';
const END_BORDER_CLASSNAME = 'end-border';

const EDGE_MARGIN = 85;

// function _prepareEvent(event) {
//   if ('originalEvent' in event) {
//     event = event.originalEvent;
//   }

//   if ('pointers' in event) {
//     event = event.pointers[0];
//   }

//   if (event.targetTouches && event.targetTouches.length >= 1) {
//     event = event.targetTouches[0];
//   }

//   return event;
// }

class SelectionContext {
  constructor(context) {
    this.hasTouchEvents = context?.hasTouchEvents ?? false;
    this.isIpad = context?.isIpad ?? false;
  }
}

class BookSelection {
  constructor(selector, selectionContext = {}) {
    this.highlights = [];
    this.containerSelector = selector;
    this.isActive = false;
    this.startLocator = null;
    this.decoratedLocator = null;
    this.queriedScroll = null;
    this.selectionMode = SelectionModeEnum.COMMON;
    this.direction = SelectionDirectionEnum.INLINE;
    this.SCROLL_TRIGGER_EVENT = selectionContext.isDevice
      ? 'touchmove'
      : 'mousemove';
    this.shouldAddPointerEvents =
      typeof window !== 'undefined' &&
      'PointerEvent' in window &&
      selectionContext.isIpad;
    this.notaClassName =
      selectionContext?.highlightClassName ??
      SelectionConstantUtils.SELECTIONS_CLASS;
    this.clickedOutsidePara = false;
    this.isBordersExist = selectionContext.isBordersExist ?? true;
    this.startBorder = null;
    this.endBorder = null;
    this.startPosition = {};
    this.currentPositionOutside = { topOutside: false, bottomOutside: false };
    this.trackDirection = true;
    this.fullSelected = false;
  }

  reinitBorders() {
    const borderInitFn = event => this.initSelection.call(this, event);
    if (this.startBorder) {
      this.startBorder.removeEventListener('mousedown', borderInitFn);
    }
    if (this.endBorder) {
      this.endBorder.removeEventListener('mousedown', borderInitFn);
    }

    this.startBorder = this._createBorder(START_BORDER_CLASSNAME, borderInitFn);
    this.endBorder = this._createBorder(END_BORDER_CLASSNAME, borderInitFn);
  }

  setScrollTriggerEvent(event) {
    if (event) {
      this.SCROLL_TRIGGER_EVENT = event;
    }
  }

  setStartPosition(event) {
    this.startPosition = this.getCurrentPosition(event);
  }

  _createBorder(className, initSelectionFn) {
    const border = document.querySelector('.' + className);
    if (!border) {
      return;
    }
    const borderClone = border.cloneNode();
    borderClone.classList.add(className);
    borderClone.addEventListener('mousedown', initSelectionFn);
    borderClone.addEventListener('touchstart', initSelectionFn);
    if (this.shouldAddPointerEvents) {
      borderClone.addEventListener('pointerdown', initSelectionFn);
    }
    const container = document.querySelector(this.containerSelector);
    if (container) {
      container.appendChild(borderClone);
    }

    border.remove();
    return borderClone;
  }

  initSelection(event) {
    event.preventDefault();
    event.stopPropagation();
    this.isActive = true;
    this.direction = this.getDirection(event);
    this.selectionMode = SelectionModeEnum.BORDER;
  }

  getCurrentPosition(event) {
    if (
      event.type === 'mousedown' ||
      event.type === 'mousemove' ||
      event.type === 'dblclick' ||
      event.type === 'pointermove' ||
      event.type === 'pointerdown'
    ) {
      return event.clientY;
    } else if (event.type === 'touchmove' || event.type === 'touchstart') {
      return event.touches[0]?.clientY;
    }

    return null;
  }

  getDirection(event, currentPosition) {
    if (
      currentPosition < this.startPosition ||
      this.isStartBorder(event.target)
    ) {
      return SelectionDirectionEnum.TOP;
    } else if (
      currentPosition > this.startPosition ||
      this.isEndBorder(event.target)
    ) {
      return SelectionDirectionEnum.BOTTOM;
    } else {
      return SelectionDirectionEnum.INLINE;
    }
  }

  isStartBorder(element) {
    if (!this.isBordersExist) {
      return false;
    }
    return element.classList.contains(START_BORDER_CLASSNAME);
  }

  isEndBorder(element) {
    if (!this.isBordersExist) {
      return false;
    }
    return element.classList.contains(END_BORDER_CLASSNAME);
  }

  activateScrollListener() {
    this.isActive = true;
    window.document.addEventListener(this.SCROLL_TRIGGER_EVENT, event => {
      this.edgeScroll.call(this, event);
    });
  }

  resetCurrentPositionOutside() {
    this.currentPositionOutside = {
      topOutside: false,
      bottomOutside: false
    };
  }

  resetSelection() {
    this.fullSelected = false;
    this.decoratedLocator = null;
    this.startLocator = null;
    this.direction = SelectionDirectionEnum.INLINE;
    this.resetCurrentPositionOutside();
    this.undecorate();
  }

  deactivateScrollListener() {
    this.isActive = false;
    window.document.removeEventListener(
      this.SCROLL_TRIGGER_EVENT,
      this.edgeScroll
    );
  }

  undecorate() {
    if (!this.isBordersExist) {
      return;
    }
    this._hideBorders();
    this.trackDirection = true;
    const container = document.querySelector(this.containerSelector);
    if (!container) {
      return;
    }
    Highlighter.undecorateByClass(this.notaClassName, container);
  }

  _hideBorders() {
    this._setCss(this.endBorder, { display: 'none' });
    this._setCss(this.startBorder, { display: 'none' });
  }

  _setAttr(el, attrs) {
    for (let attrName in attrs) {
      el.setAttribute(attrName, attrs[attrName]);
    }
  }

  _setCss(el, styles) {
    if (!el) {
      return;
    }
    for (let prop in styles) {
      el.style[prop] = styles[prop];
    }
  }

  startSelection(event) {
    this.undecorate();
    this.startLocator = this._findLocatorByEvent(event);
    this.clickedOutsidePara = this._getClickedOutsidePara(event);
    if (this.startLocator === null || this.clickedOutsidePara) {
      throw CustomErrorEnum.UNSELECTABLE_ELEMENTS;
    }
    this.decoratedLocator = null;
    this.isActive = true;
    this.selectionMode = SelectionModeEnum.COMMON;
  }

  processSelection(event) {
    if (this._isParagraphDisabled(event.target)) {
      throw CustomErrorEnum.UNSELECTABLE_ELEMENTS;
    }
  }

  _isParagraphDisabled(element) {
    return MarkerUtils.isDisabledParagraph(element);
  }

  _getClickedOutsidePara(event) {
    const isWrapperClose = event.target.closest(
      `.${SelectionConstantUtils.SCROLL_ITEM_WRAPPER_CLASS}`
    );
    const isContextMenuClose =
      event.target.closest(`.${SelectionConstantUtils.CONTEXT_MENU}`) ||
      event.target
        .closest(`.${SelectionConstantUtils.SELECTION_COMMON_CONTAINER_CLASS}`)
        ?.querySelector(`.${SelectionConstantUtils.CONTEXT_MENU}`);
    return !isWrapperClose && !isContextMenuClose;
  }

  _findLocatorByEvent(event) {
    const container = document.querySelector(this.containerSelector);
    if (container === null) {
      return null;
    }
    const { clientX, clientY } = MarkerUtils.getCoordinatesFromEvent(event);

    const elements = MarkerUtils.getParagraphElements(container);
    if (elements.length === 0) {
      return null;
    }
    const position = LayoutUtils.findLogicalPositionByPoint(
      clientX,
      clientY,
      elements,
      container,
      {
        forceLayoutUsage: false
      }
    );

    if (position === null) {
      return null;
    }
    let isEmptyBlock = true;
    if (position.element) {
      isEmptyBlock = position.element.innerText.length === 0;
    }
    if (isEmptyBlock) {
      return null;
    }
    return LayoutUtils.convertPositionToRangeLocator(position, elements);
  }

  addHighlightByEvent(event) {
    if (!this.isActive) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this._decorateByEvent(event);
  }

  isTopOutside(position) {
    return position < SelectionConstantUtils.TOOLBAR_HEIGHT + 10;
  }

  isBottomOutside(position) {
    return (
      position >
      window.innerHeight - SelectionConstantUtils.PROGRESS_TOOLBAR_HEIGHT - 10
    );
  }

  isStartBorderOutside() {
    const startBorderRect = this.startBorder?.getBoundingClientRect();
    if (!startBorderRect || !startBorderRect.height) {
      return false;
    }
    return (
      this.isTopOutside(startBorderRect.top) ||
      this.isBottomOutside(startBorderRect.bottom)
    );
  }

  isEndBorderOutside() {
    const endBorderRect = this.endBorder?.getBoundingClientRect();
    if (!endBorderRect || !endBorderRect.height) {
      return false;
    }
    return (
      this.isTopOutside(endBorderRect.top) ||
      this.isBottomOutside(endBorderRect.bottom)
    );
  }

  setIsCurrentPositionOutside(currentPosition) {
    this.currentPositionOutside = {
      topOutside: this.isTopOutside(currentPosition),
      bottomOutside: this.isBottomOutside(currentPosition)
    };
  }

  isSelectionOutside() {
    const isCurrentPositionOutside =
      this.currentPositionOutside?.topOutside ||
      this.currentPositionOutside?.bottomOutside;
    const isBordersOutside =
      this.isStartBorderOutside() || this.isEndBorderOutside();
    return isCurrentPositionOutside && isBordersOutside;
  }

  _decorateByEvent(event, selectionWrapperSelector) {
    this.fullSelected = false;
    const locator = this._findLocatorByEvent(event);
    const currentPosition = this.getCurrentPosition(event);

    if (this.isStartBorder(event.target)) {
      this.trackDirection = false;
      this.direction = SelectionDirectionEnum.TOP;
    } else if (this.isEndBorder(event.target)) {
      this.trackDirection = false;
      this.direction = SelectionDirectionEnum.BOTTOM;
    }

    if (this.trackDirection) {
      this.direction = this.getDirection(event, currentPosition);
    }

    this.setIsCurrentPositionOutside(currentPosition);

    if (locator === null) {
      return;
    }

    const decoratedLocator = this.decoratedLocator;
    if (decoratedLocator && this.direction === SelectionDirectionEnum.TOP) {
      if (decoratedLocator.endLocator.follows(locator.startLocator)) {
        this.decoratedLocator = new Locator.InTextRangeLocator(
          locator.startLocator,
          decoratedLocator.endLocator
        );
      }
    } else if (
      decoratedLocator &&
      this.direction === SelectionDirectionEnum.BOTTOM
    ) {
      if (this.decoratedLocator.startLocator.precedes(locator.endLocator)) {
        this.decoratedLocator = new Locator.InTextRangeLocator(
          this.decoratedLocator.startLocator,
          locator.endLocator
        );
      }
    } else if (this.startLocator !== null) {
      const comparationResult = locator.compareTo(this.startLocator);
      if (comparationResult === 0) {
        this.decoratedLocator = this.startLocator;
      } else {
        const start = comparationResult < 0 ? locator : this.startLocator;
        const end = comparationResult > 0 ? locator : this.startLocator;
        this.decoratedLocator = new Locator.InTextRangeLocator(
          start.startLocator,
          end.endLocator
        );
      }
    }

    if (!this.decoratedLocator) {
      return;
    }

    this._normalizeDecoratedLocator();

    const container = document.querySelector(this.containerSelector);
    Highlighter.updateDecoration(
      decoratedLocator,
      this.decoratedLocator,
      container,
      this.notaClassName,
      selectionWrapperSelector
    );

    this.repaintSelectionBorder();
  }

  _normalizeDecoratedLocator() {
    const endLocator = this.decoratedLocator.endLocator;
    const startLocator = this.decoratedLocator.startLocator;

    if (startLocator.equals(endLocator)) {
      return;
    }

    if (_isFirstParaItemEmpty(endLocator)) {
      const container = document.querySelector(this.containerSelector);
      this.decoratedLocator.endLocator = LayoutUtils.getPreviousLocator(
        endLocator,
        container
      );
    }
    return;

    function _isFirstParaItemEmpty(_endLocator) {
      return (
        has(_endLocator, 'logicalCharOffset') &&
        _endLocator.logicalCharOffset === 0
      );
    }
  }

  repaintSelectionBorder() {
    if (
      this.decoratedLocator !== null &&
      !this.decoratedLocator.isCollapsed() &&
      this.isBordersExist
    ) {
      this._showSelectionBorder();
    }
  }

  _showSelectionBorder() {
    const container = document.querySelector(this.containerSelector);
    const containerRect = container.getClientRects()[0];
    const top = -1 * containerRect.top;
    const left = containerRect.left;
    const startRect = LayoutUtils.calcLocatorRectangle(
      this.decoratedLocator.startLocator,
      container
    );

    if (startRect === null) {
      return;
    }

    const endRect = LayoutUtils.calcLocatorRectangle(
      this.decoratedLocator.endLocator,
      container,
      true
    );
    if (endRect === null) {
      return;
    }

    const startBorderDir = this._getBorderDirByLocator(
      this.decoratedLocator.startLocator
    );
    const endBorderDir = this._getBorderDirByLocator(
      this.decoratedLocator.endLocator
    );

    const startBorderLeft =
      startBorderDir === 'ltr' ? startRect.left - left : startRect.right - left;

    const startBorderStyles = {
      top: top + startRect.top + 'px',
      left: startBorderLeft + 'px',
      height: startRect.height + 'px'
    };
    const endBorderLeft =
      endBorderDir === 'ltr' ? endRect.right - left : endRect.left - left;
    const endBorderStyles = {
      top: top + endRect.top + 'px',
      left: endBorderLeft + 'px',
      height: endRect.height + 'px'
    };

    this._setAttr(this.startBorder, {
      dir: startBorderDir
    });
    this._setAttr(this.endBorder, {
      dir: endBorderDir
    });

    this._setCss(this.startBorder, startBorderStyles);
    this._setCss(this.endBorder, endBorderStyles);
    this._showBorders();
  }

  _getBorderDirByLocator(locator) {
    const element = LayoutUtils.getElementByLocator(locator);
    const paraItem = element?.closest('.item');
    return (
      this._detectTextDirection(element?.innerText) ||
      paraItem?.getAttribute('dir') ||
      'ltr'
    );
  }

  _detectTextDirection(text) {
    if (!text) {
      return;
    }
    const rtlPattern = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/;
    if (rtlPattern.test(text)) {
      return 'rtl';
    } else {
      return 'ltr';
    }
  }

  _getBorderDirByPoint(x, y) {
    const elements = document.elementsFromPoint(x, y);
    const item = elements.find(element => {
      return element.classList.contains('item');
    });
    let dir = 'ltr';
    if (item) {
      dir = item.getAttribute('dir');
    }
    return dir;
  }

  _showBorders() {
    this._setCss(this.endBorder, { display: '' });
    this._setCss(this.startBorder, { display: '' });
  }

  complete() {
    this.isActive = false;
    this.fullSelected = true;
    this.handledEvent = null;
    return {
      locator: this.decoratedLocator,
      selectionMode: this.selectionMode
    };
  }

  edgeScroll(event) {
    if (!this.decoratedLocator) {
      return;
    }

    const { clientY } = MarkerUtils.getCoordinatesFromEvent(event);
    const viewportBottomEdgeY = window.innerHeight - EDGE_MARGIN;
    if (clientY > EDGE_MARGIN && clientY < viewportBottomEdgeY) {
      window.clearTimeout(this.queriedScroll);
      return;
    }

    const delta =
      clientY - (clientY <= EDGE_MARGIN ? EDGE_MARGIN : viewportBottomEdgeY);
    event.stopPropagation();
    event.preventDefault();
    this._scrollOnSelection(delta * 1.05, event);
  }

  _scrollOnSelection(delta, event) {
    if (!this.isActive) {
      return;
    }
    const container = document.querySelector(this.containerSelector);
    let wrapper = container.parentElement;

    delta = Math.round(delta);
    let scrollTop = wrapper.scrollTop;
    wrapper.scrollTo(0, scrollTop + delta);

    this.update(event);

    window.clearTimeout(this.queriedScroll);
    this.queriedScroll = window.setTimeout(() => {
      this._scrollOnSelection(delta, event);
    }, 50);
  }

  update(event) {
    if (!this.isActive) {
      return;
    }

    this._decorateByEvent(event);
  }
}

export { BookSelection, SelectionContext };
